import { z } from 'zod'
import type { StepType } from '../../types/workflow'

// Step type enum
export const stepTypeSchema = z.enum([
  'mouse_click',
  'mouse_double_click',
  'mouse_down',
  'mouse_up',
  'mouse_move',
  'drag_drop',
  'scroll',
  'type_text',
  'keyboard_shortcut',
  'get_clipboard',
  'set_clipboard',
  'copy_selected_text',
  'paste_text',
  'navigate_url',
  'take_screenshot',
  'task',
  'verify',
  'notify'
])

// Parameter validation schemas
export const mouseClickParamsSchema = z.object({
  selector: z.string().optional(),
  x: z.number().min(0).optional(),
  y: z.number().min(0).optional(),
  waitFor: z.number().min(0).max(30000).optional() // max 30 seconds
}).refine(
  (data) => data.selector || (data.x !== undefined && data.y !== undefined),
  {
    message: "Either selector or x,y coordinates must be provided",
    path: ["selector"]
  }
)

export const mouseDoubleClickParamsSchema = z.object({
  selector: z.string().optional(),
  x: z.number().min(0).optional(),
  y: z.number().min(0).optional()
}).refine(
  (data) => data.selector || (data.x !== undefined && data.y !== undefined),
  {
    message: "Either selector or x,y coordinates must be provided",
    path: ["selector"]
  }
)

export const mouseDownParamsSchema = z.object({
  selector: z.string().optional(),
  x: z.number().min(0).optional(),
  y: z.number().min(0).optional()
}).refine(
  (data) => data.selector || (data.x !== undefined && data.y !== undefined),
  {
    message: "Either selector or x,y coordinates must be provided",
    path: ["selector"]
  }
)

export const mouseUpParamsSchema = z.object({
  selector: z.string().optional(),
  x: z.number().min(0).optional(),
  y: z.number().min(0).optional()
}).refine(
  (data) => data.selector || (data.x !== undefined && data.y !== undefined),
  {
    message: "Either selector or x,y coordinates must be provided",
    path: ["selector"]
  }
)

export const mouseMoveParamsSchema = z.object({
  selector: z.string().optional(),
  x: z.number().min(0).optional(),
  y: z.number().min(0).optional()
}).refine(
  (data) => data.selector || (data.x !== undefined && data.y !== undefined),
  {
    message: "Either selector or x,y coordinates must be provided",
    path: ["selector"]
  }
)

export const dragDropParamsSchema = z.object({
  sourceSelector: z.string().optional(),
  sourceX: z.number().min(0).optional(),
  sourceY: z.number().min(0).optional(),
  targetSelector: z.string().optional(),
  targetX: z.number().min(0).optional(),
  targetY: z.number().min(0).optional()
}).refine(
  (data) => {
    const hasSourceSelector = !!data.sourceSelector
    const hasSourceCoords = data.sourceX !== undefined && data.sourceY !== undefined
    const hasTargetSelector = !!data.targetSelector
    const hasTargetCoords = data.targetX !== undefined && data.targetY !== undefined
    
    return (hasSourceSelector || hasSourceCoords) && (hasTargetSelector || hasTargetCoords)
  },
  {
    message: "Both source and target must have either selector or coordinates",
    path: ["sourceSelector"]
  }
)

export const scrollParamsSchema = z.object({
  x: z.number().min(0).max(5000).optional(), // X coordinate (0-5000 pixels)
  y: z.number().min(0).max(5000).optional(), // Y coordinate (0-5000 pixels)
  deltaX: z.number().min(-1000).max(1000).optional(), // Horizontal scroll amount
  deltaY: z.number().min(-1000).max(1000).optional(), // Vertical scroll amount
  steps: z.number().min(1).max(100).optional() // Number of scroll steps
})

export const typeTextParamsSchema = z.object({
  selector: z.string().min(1, "Selector is required"),
  text: z.string().min(1, "Text is required"),
  clearFirst: z.boolean().optional()
})

export const keyboardShortcutParamsSchema = z.object({
  keys: z.string().min(1, "Key combination is required")
    .regex(/^(Ctrl|Alt|Shift|Meta)(\+(Ctrl|Alt|Shift|Meta))*\+[a-zA-Z0-9]$|^[a-zA-Z0-9]$|^(Enter|Escape|Tab|Space|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Home|End|PageUp|PageDown|Delete|Backspace)$/, 
      "Invalid key combination format")
})

export const getClipboardParamsSchema = z.object({
  variableName: z.string().optional()
})

export const setClipboardParamsSchema = z.object({
  text: z.string().min(1, "Text is required")
})

export const copySelectedTextParamsSchema = z.object({})

export const pasteTextParamsSchema = z.object({})

export const navigateUrlParamsSchema = z.object({
  url: z.string().min(1, "URL is required").refine(
    (url) => {
      try {
        // Allow URLs without protocol - add https:// if missing
        const testUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`
        new URL(testUrl)
        return true
      } catch {
        return false
      }
    },
    { message: "Must be a valid URL" }
  ),
  waitForLoad: z.number().min(0).max(60000).optional() // max 60 seconds
})

export const takeScreenshotParamsSchema = z.object({
  filename: z.string().optional()
})

export const taskParamsSchema = z.object({
  description: z.string().min(1, "Task description is required")
})

export const verifyParamsSchema = z.object({
  description: z.string().min(1, "Verification description is required")
})

export const notifyParamsSchema = z.object({
  message: z.string().optional()
})

// Union schema for all step parameters
export const stepParamsSchema = z.discriminatedUnion('step_type', [
  z.object({ step_type: z.literal('mouse_click'), params: mouseClickParamsSchema }),
  z.object({ step_type: z.literal('mouse_double_click'), params: mouseDoubleClickParamsSchema }),
  z.object({ step_type: z.literal('mouse_down'), params: mouseDownParamsSchema }),
  z.object({ step_type: z.literal('mouse_up'), params: mouseUpParamsSchema }),
  z.object({ step_type: z.literal('mouse_move'), params: mouseMoveParamsSchema }),
  z.object({ step_type: z.literal('drag_drop'), params: dragDropParamsSchema }),
  z.object({ step_type: z.literal('scroll'), params: scrollParamsSchema }),
  z.object({ step_type: z.literal('type_text'), params: typeTextParamsSchema }),
  z.object({ step_type: z.literal('keyboard_shortcut'), params: keyboardShortcutParamsSchema }),
  z.object({ step_type: z.literal('get_clipboard'), params: getClipboardParamsSchema }),
  z.object({ step_type: z.literal('set_clipboard'), params: setClipboardParamsSchema }),
  z.object({ step_type: z.literal('copy_selected_text'), params: copySelectedTextParamsSchema }),
  z.object({ step_type: z.literal('paste_text'), params: pasteTextParamsSchema }),
  z.object({ step_type: z.literal('navigate_url'), params: navigateUrlParamsSchema }),
  z.object({ step_type: z.literal('take_screenshot'), params: takeScreenshotParamsSchema }),
  z.object({ step_type: z.literal('task'), params: taskParamsSchema }),
  z.object({ step_type: z.literal('verify'), params: verifyParamsSchema }),
  z.object({ step_type: z.literal('notify'), params: notifyParamsSchema })
])

// Workflow step schema
export const workflowStepSchema = z.object({
  id: z.string().optional(), // Optional for creation
  workflow_id: z.number(),
  step_index: z.number().min(0),
  step_type: stepTypeSchema,
  params: z.record(z.any()), // Will be validated by specific param schema
  enabled: z.boolean().default(true)
})

// Workflow step creation schema
export const createWorkflowStepSchema = z.object({
  step_index: z.number().min(0),
  step_type: stepTypeSchema,
  params: z.record(z.any()),
  enabled: z.boolean().default(true)
})

// Workflow step update schema
export const updateWorkflowStepSchema = z.object({
  step_index: z.number().min(0).optional(),
  step_type: stepTypeSchema.optional(),
  params: z.record(z.any()).optional(),
  enabled: z.boolean().optional()
})

// Helper function to get parameter schema for a specific step type
export function getStepParamsSchema(stepType: StepType) {
  switch (stepType) {
    case 'mouse_click':
      return mouseClickParamsSchema
    case 'mouse_double_click':
      return mouseDoubleClickParamsSchema
    case 'mouse_down':
      return mouseDownParamsSchema
    case 'mouse_up':
      return mouseUpParamsSchema
    case 'mouse_move':
      return mouseMoveParamsSchema
    case 'drag_drop':
      return dragDropParamsSchema
    case 'scroll':
      return scrollParamsSchema
    case 'type_text':
      return typeTextParamsSchema
    case 'keyboard_shortcut':
      return keyboardShortcutParamsSchema
    case 'get_clipboard':
      return getClipboardParamsSchema
    case 'set_clipboard':
      return setClipboardParamsSchema
    case 'copy_selected_text':
      return copySelectedTextParamsSchema
    case 'paste_text':
      return pasteTextParamsSchema
    case 'navigate_url':
      return navigateUrlParamsSchema
    case 'take_screenshot':
      return takeScreenshotParamsSchema
    case 'task':
      return taskParamsSchema
    case 'verify':
      return verifyParamsSchema
    case 'notify':
      return notifyParamsSchema
    default:
      throw new Error(`Unknown step type: ${stepType}`)
  }
}

// Workflow validation function
export function validateWorkflow(steps: any[]): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!steps || steps.length === 0) {
    errors.push('Workflow must have at least one step')
    return { isValid: false, errors }
  }

  // Check for start step
  const startStep = steps.find(step => step.id === 'start-block' || step.step_type === 'navigate_url')
  if (!startStep) {
    errors.push('Workflow must have a start step')
  }

  // Check each step
  steps.forEach((step, index) => {
    if (!step.step_type) {
      errors.push(`Step ${index + 1} is missing step_type`)
    }

    if (!step.enabled) {
      return // Skip disabled steps
    }

    // Validate step parameters based on type
    try {
      const schema = getStepParamsSchema(step.step_type)
      const result = schema.safeParse(step.params)
      if (!result.success) {
        errors.push(`Step ${index + 1} (${step.step_type}): ${result.error.message}`)
      }
    } catch (error) {
      errors.push(`Step ${index + 1}: Invalid step type ${step.step_type}`)
    }
  })

  return {
    isValid: errors.length === 0,
    errors
  }
}
